import { useCallback, useEffect } from 'react';
import { Grid, Stack, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchAdmin, toErrorMessage } from '../../admin/api';
import { PATH_DASHBOARD } from '../../routes/paths';
import { AdminDataTable } from '../../sections/admin/shared';
import ConfigGroupDrawer from '../../sections/admin/config/ConfigGroupDrawer';
import { AdminRouteLayout, RequestState, useAsyncState } from './AdminRoutePageShared';

export default function ConfigGroupsPage() {
  const navigate = useNavigate();
  const { groupKey } = useParams();
  const groups = useAsyncState({ groups: [], feature_flags: [] });
  const {
    state: groupsState,
    setLoading: setGroupsLoading,
    setData: setGroupsData,
    setError: setGroupsError,
  } = groups;

  const loadGroups = useCallback(async () => {
    setGroupsLoading();
    try {
      const response = await fetchAdmin('/config/groups');
      setGroupsData(response);
    } catch (error) {
      setGroupsError(toErrorMessage(error));
    }
  }, [setGroupsData, setGroupsError, setGroupsLoading]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  return (
    <AdminRouteLayout title="Config Overview" breadcrumbTitle="Config">
      <RequestState loading={groupsState.loading} error={groupsState.error}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={12}>
            <AdminDataTable
              title="Config Groups"
              description="Select a config group to open its runtime workspace in a side drawer. The drawer keeps raw items and resolved runtime output together without forcing a page change."
              columns={[
                { field: 'label', headerName: 'Label' },
                { field: 'groupKey', headerName: 'Group Key' },
                { field: 'description', headerName: 'Description' },
                { field: 'system', headerName: 'System' },
                { field: 'updatedAt', headerName: 'Updated At' },
              ]}
              onRowClick={(row) => navigate(PATH_DASHBOARD.config.group(row.groupKey))}
              rows={(groupsState.data?.groups || []).map((row) => ({
                id: row.id,
                label: row.label,
                groupKey: row.group_key,
                description: row.description || 'No description returned',
                system: row.is_system ? 'system' : 'custom',
                updatedAt: row.updated_at || 'N/A',
              }))}
              emptyTitle="No config groups"
              emptyDescription="The config groups endpoint returned no groups."
            />
            <Stack spacing={1} sx={{ px: 1, pt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Use the row itself as the entry point. Selecting a group opens its full operational context in the side drawer.
              </Typography>
            </Stack>
          </Grid>
          {/* <Grid item xs={12} lg={4}>
            <AdminDataTable
              title="Feature Flag Summary"
              description="Compact summary from the grouped overview response. Use the dedicated Feature Flags screen for live toggles."
              columns={[
                { field: 'label', headerName: 'Label' },
                { field: 'flagKey', headerName: 'Flag Key' },
                { field: 'enabled', headerName: 'Enabled' },
              ]}
              rows={(groupsState.data?.feature_flags || []).map((flag) => ({
                id: flag.id,
                label: flag.label,
                flagKey: flag.flag_key,
                enabled: Number(flag.is_enabled) ? 'enabled' : 'disabled',
              }))}
              emptyTitle="No feature flags"
              emptyDescription="The grouped config overview returned no feature-flag summary rows."
            />
            <Stack spacing={1} sx={{ px: 1, pt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                The contract keeps feature flag management on the dedicated `GET /config/feature_flags` and `PUT /config/feature_flags/{'{flagKey}'}` routes, even though this overview also returns a summary.
              </Typography>
            </Stack>
          </Grid> */}
        </Grid>
      </RequestState>

      <ConfigGroupDrawer
        open={Boolean(groupKey)}
        groupKey={groupKey}
        onClose={() => navigate(PATH_DASHBOARD.config.groups)}
        onItemUpdated={loadGroups}
      />
    </AdminRouteLayout>
  );
}
